import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowRight, CircleX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AUDITED_FIELDS,
  useBoxAudit,
  type BoxAuditAction,
  type BoxAuditEntry,
} from '@/lib/api/boxAudit';

const ACTION_BADGE: Record<
  BoxAuditAction,
  'default' | 'secondary' | 'destructive'
> = {
  create: 'default',
  update: 'secondary',
  delete: 'destructive',
};

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  ip: 'IP',
  group: 'Group',
  tv: 'TV',
  brand: 'Type',
  icon: 'Icon',
};

const ALL = '__all';

function Changes({ entry }: { entry: BoxAuditEntry }) {
  if (!entry.changes.length)
    return <span className="text-muted-foreground">—</span>;
  return (
    <div className="grid gap-0.5">
      {entry.changes.map((c, i) => (
        <div key={i} className="flex items-center gap-1.5 text-sm">
          <span className="w-12 shrink-0 text-muted-foreground">
            {FIELD_LABELS[c.field] ?? c.field}
          </span>
          <span className="font-mono">{c.from}</span>
          <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="font-mono">{c.to}</span>
        </div>
      ))}
    </div>
  );
}

/** Admin-only audit timeline of every box create/update/delete. */
export function BoxHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [max, setMax] = useState(300);
  const [entries, loading] = useBoxAudit(max);

  const boxFilter = searchParams.get('box') ?? '';
  const [action, setAction] = useState<string>(ALL);
  const [field, setField] = useState<string>(ALL);
  const [user, setUser] = useState<string>(ALL);

  const users = useMemo(
    () =>
      [
        ...new Set((entries ?? []).map((e) => e.byEmail).filter(Boolean)),
      ].sort(),
    [entries]
  );

  const filtered = useMemo(() => {
    const q = boxFilter.trim().toLowerCase();
    return (entries ?? []).filter(
      (e) =>
        (!q || e.boxName.toLowerCase().includes(q)) &&
        (action === ALL || e.action === action) &&
        (user === ALL || e.byEmail === user) &&
        (field === ALL || e.changes.some((c) => c.field === field))
    );
  }, [entries, boxFilter, action, user, field]);

  const setBoxFilter = (value: string) =>
    setSearchParams(value ? { box: value } : {}, { replace: true });

  const filtering =
    !!boxFilter || action !== ALL || field !== ALL || user !== ALL;

  return (
    <div className="w-full pb-4">
      <h1 className="text-3xl font-bold">Box History</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Append-only audit log of box changes — who changed what, and when.
        Entries can&apos;t be edited or removed.
      </p>
      <Separator className="my-6" />

      <div className="flex flex-wrap items-center gap-2 pb-4">
        <Input
          placeholder="Filter box name…"
          value={boxFilter}
          onChange={(e) => setBoxFilter(e.target.value)}
          className="max-w-xs"
        />
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All actions</SelectItem>
            <SelectItem value="create">Created</SelectItem>
            <SelectItem value="update">Updated</SelectItem>
            <SelectItem value="delete">Deleted</SelectItem>
          </SelectContent>
        </Select>
        <Select value={field} onValueChange={setField}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All fields</SelectItem>
            {AUDITED_FIELDS.map((f) => (
              <SelectItem key={f} value={f}>
                {FIELD_LABELS[f] ?? f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={user} onValueChange={setUser}>
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All users</SelectItem>
            {users.map((u) => (
              <SelectItem key={u} value={u}>
                {u}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filtering && (
          <Button
            variant="ghost"
            className="h-8 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              setBoxFilter('');
              setAction(ALL);
              setField(ALL);
              setUser(ALL);
            }}
          >
            Reset
            <CircleX className="ml-2 h-4 w-4" />
          </Button>
        )}
        <span className="ml-auto text-sm text-muted-foreground">
          {filtered.length} of {(entries ?? []).length} entries
        </span>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px]">Time</TableHead>
              <TableHead className="w-[220px]">User</TableHead>
              <TableHead className="w-[220px]">Box</TableHead>
              <TableHead className="w-[90px]">Action</TableHead>
              <TableHead>Changes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  {loading
                    ? 'Loading…'
                    : filtering
                      ? 'No entries match the filters.'
                      : 'No changes recorded yet.'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="whitespace-nowrap align-top text-muted-foreground">
                    {e.at ? format(e.at, 'MMM d, yyyy HH:mm') : '—'}
                  </TableCell>
                  <TableCell className="align-top">{e.byEmail}</TableCell>
                  <TableCell className="align-top font-medium">
                    {e.boxName}
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant={ACTION_BADGE[e.action]}>{e.action}</Badge>
                  </TableCell>
                  <TableCell>
                    <Changes entry={e} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {(entries ?? []).length === max && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" onClick={() => setMax(max + 300)}>
            Load older entries
          </Button>
        </div>
      )}
    </div>
  );
}

export default BoxHistory;
